import type { DomElement } from './devigner';
import { Style } from './style';

//-----------------------------------------------------------------------------
// auth
//-----------------------------------------------------------------------------
export interface AuthUser {
	id: string;
	name: string;
	email: string;
	permissions: string[];
	// False when an environment allowlist (forge ALLOWED_EMAILS) excludes this
	// account; studio blocks the UI on it. Always true when no allowlist is set.
	accessAllowed: boolean;
}

//-----------------------------------------------------------------------------
// user
//-----------------------------------------------------------------------------

// Contact-card projection of a user: identity only, never the full row.
export interface UserContact {
	id: string;
	displayName: string;
	email: string;
}

export enum Tier {
	FREE = 0,
	TIER_1 = 1,
	TIER_2 = 2,
}

export enum TierInterval {
	MONTH = 'month',
	YEAR = 'year',
}

export interface Subscription {
	userId: string;
	tier: Tier;
	stripeSubscriptionId?: string;
	createdAt: Date;
	updatedAt: Date;
	amount?: number;
	interval?: TierInterval;
	currentPeriodEnd?: Date;
	intervalType?: TierInterval;
	periodStartDate?: Date;
	nextCreditGrantDate?: Date;
	creditsGrantedCount?: number;
}

export interface User {
	id: string;
	displayName?: string;
	email?: string;
	metadata: UserMetadata;
	stripeCustomerId?: string;
	auth0AlternateId?: string;
	createdAt: Date;
	updatedAt?: Date;
	writeId: string;
}

export type UserSettings = {
	visualEditsEnabled?: boolean;
	commentEditingEnabled?: boolean;
	debugModeEnabled?: boolean;
	defaultDesignSystemId?: string | null;
	defaultOrganizationId?: string | null;
};

export const DEFAULT_USER_SETTINGS: Required<UserSettings> = {
	visualEditsEnabled: true,
	commentEditingEnabled: false,
	debugModeEnabled: false,
	defaultDesignSystemId: null,
	defaultOrganizationId: null,
};

export type OAuthCredentials = {
	accessToken: string;
	refreshToken: string;
	expiresAt: string;
};

export type GitHubCredentials = {
	installationId: number;
	accountLogin: string;
};

export type UserCredentials = {
	figma?: OAuthCredentials;
	github?: GitHubCredentials;
};

export type UserMetadata = {
	numberOfExports?: number;
	lastExportDate?: string;
	numberOfEdits?: number;
	lastEditDate?: string;
	lastSharedDate?: string;
	lastSceneId?: string;
	settings?: UserSettings;
	isStripeSandboxCustomer?: boolean;
	credentials?: UserCredentials; // credentials should not be sent to studio. they often contain sensitive data like access tokens.
	recentExports?: ExportLink[];
};

export type ExportLinkStatus = 'pending' | 'ready' | 'failed';

export type ExportLink = {
	id: string;
	filename: string;
	// null while status === 'pending' or 'failed' (no file written yet / not written)
	filePath: string | null;
	label: string;
	createdAt: string;
	downloadedAt: string | null;
	// Absent on legacy rows; read sites must treat undefined as 'ready'.
	status?: ExportLinkStatus;
	// Short error message when status === 'failed'.
	error?: string;
	// Stored artifact size. Absent on legacy rows and while status === 'pending'.
	sizeBytes?: number;
};

export interface ReferralInfo {
	referralCode: string;
	totalReferrals: number;
	subscribedReferrals: number;
	creditsEarned: number;
}

export type Profile = {
	user: User;
	userSceneIds: string[];
	userDesignSystemIds: string[];
	scenes: Scene[];
	designSystems: DesignSystem[];
	organizations: (Organization & { role: OrganizationRole })[];
	organizationSceneIds: Record<string, string[]>; // organizationId -> sceneIds
	organizationDesignSystemIds: Record<string, string[]>; // organizationId -> designSystemIds
	creditWallet?: CreditWallet;
	organizationCreditWallets?: Record<string, OrganizationCreditWallet>; // organizationId -> wallet
	figmaConnected?: boolean;
	githubConnected?: boolean;
	referralInfo?: ReferralInfo;
	writeIds: string[];
	timestamp: Date;
};

//-----------------------------------------------------------------------------
// design system
//-----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
export interface DesignSystem {
	id: string;
	title: string;
	writeId: string;
	createdAt: Date;
	updatedAt: Date;
	metadata: DesignSystemMetadata;
}

export interface DesignSystemMetadata {
	pendingEmails?: string[];
	// store timestamps at which the user opened the design system, keyed by userId
	openActions?: Record<string, Date>;
	publiclyReadable?: boolean;
}

export interface DesignSystemVersion {
	id: string;
	designSystemId: string;
	versionNumber: number;
	createdAt: Date;
	updatedAt: Date;
	manifest: DesignSystemManifest;
	// Each DesignSystemVersion has an associated set of DsFiles.
	// This connection does not exist in a data type here.
	// Instead they are implicitly connected via the ds_version_id field in the ds_files table.
	// Version 0 (draft) is identified by versionNumber === 0, no separate draft field needed.
}

export interface DsVersionRef {
	versionNumber: number;
	createdAt: Date;
}

// The resolved design system a scene is using (or about to use).
// Contains everything needed to display, link, and manage the DS from a scene's perspective.
export interface SceneDsLink {
	designSystem: DesignSystem;
	linkedVersion: DesignSystemVersion;
	latestPublishedVersion: DesignSystemVersion;
	hasDraft: boolean;
}

// DsFiles are the internal representation of a design system
// (what a user edits in the DesignSystemEditor - the core source of truth)
export interface DsFile {
	path: string;
	content: string;
}

// A change record for a single DS file between draft and published versions.
// `prior` carries the published content for modified/removed files;
// and is undefined for added files
export interface DsFileChange {
	path: string;
	status: 'added' | 'modified' | 'removed';
	prior?: string;
}

// A manifest is the set of files needed to utilize a design system
export type IconPackId = 'material-symbols' | 'phosphor' | 'lucide' | 'font-awesome';

// Design system font configuration (runtime loading info)
// Tells the runtime how to load fonts referenced in theme/typography.css
export interface DesignSystemFont {
	fontFamily: string; // CSS font-family value: "Inter", "Playfair Display"
	source: 'google' | 'custom'; // How to load: Google Fonts API or custom files
	fontWeight?: string[]; // CSS font-weight values to load: ["400", "500", "700"]
	fontStyle?: ('normal' | 'italic')[]; // CSS font-style values to load
	fallback?: string; // Generic fallback: "sans-serif", "serif", "monospace"
	// For custom fonts - CAS hashes for each format
	files?: {
		woff2?: string; // Preferred format
		woff?: string; // Fallback format
	};
}

export interface DsMode {
	id: string; // mode id - e.g. "light", "dark"
	label: string; // user-facing label
	default?: boolean; // if true, this is the default mode for this DS
	bg?: string; // editor preview background color (hex)
}

// Plan for creating a design system
export interface DsPlan {
	description: string;
	attachments: Attachment[];
	title: string;
	iconPack: IconPackId;
	fonts: DesignSystemFont[];
	keyComponents: string[];
}

export interface DesignSystemManifest {
	theme?: string;
	styles?: string;
	componentsCss?: string;
	componentsJs?: string;
	// LEGACY: chatPrompt, designPrompt, and promptGenerationStatus are remnants of the
	// removed DS prompt-generation job. promptGenerationStatus is hardcoded to 'complete'
	// on publish, and designSystemPromptMessage still reads the designPrompt hash from
	// old DSes. Safe to remove once all legacy design systems are migrated off these
	// fields (schema column drop + backfill pass + purge of downstream readers).
	chatPrompt?: string;
	designPrompt?: string;
	promptGenerationStatus: 'not_generated' | 'pending' | 'generating' | 'complete' | 'failed';
	// Completion flag, not a transition flag. Only set to true by the migration handler on success.
	// A "migrating" status was rejected because a crash would leave it stuck forever.
	// Instead, a Redis lock with TTL guards against duplicate in-flight calls.
	promptsMigrated?: boolean;
}

//-----------------------------------------------------------------------------
// scene
//-----------------------------------------------------------------------------
export type PartialScene = Partial<Scene> & { id: string };
export type PartialDesignSystem = Partial<DesignSystem> & { id: string };

export interface Scene {
	id: string;
	title: string;
	metadata: SceneMetadata;
	createdAt: Date;
	updatedAt?: Date;
	writeId: string;
}

export type SceneMetadata = {
	canvasColor: string;
	canvasDropShadowMode: DropShadowMode;
	devices: Device[];
	currentStyle: number | null;
	styles: Style[];
	pendingEmails?: string[];
	// store timestamps at which the user opened the scene, keyed by userId
	openActions?: Record<string, Date>;
	publiclyReadable?: boolean;
	designSystemVersionId?: string;
};

export enum DropShadowMode {
	NONE = 'none',
	LIGHT = 'light',
	MEDIUM = 'medium',
	HEAVY = 'heavy',
}

export interface SceneUser {
	sceneId: string;
	userId: string;
	writeId: string;
	createdAt: Date;
	updatedAt?: Date;
	deletedBy?: string;
	deletedAt?: Date;
}

//-----------------------------------------------------------------------------
// frame
//-----------------------------------------------------------------------------
export type PartialFrame = Partial<Frame> & { id: string };
// gap between frames, in pixels
export const FRAME_GAP = 50;

export const CHAT_COLUMN_GAP = 200;

export interface Frame {
	id: string;
	sceneId: string;
	title: string;
	x: number;
	y: number;
	width: number;
	height: number;
	imagePath?: string; // Deprecated field. Set to undefined. Now forge has a dedicated route /frame/${frame.id}/image that returns the frame's image
	siteId?: string;
	entries?: Array<FrameEntry>;
	metadata: FrameMetadata;
	createdAt: Date;
	updatedAt?: Date;
	deletedAt: Date | null;
	writeId: string;
}
export type FrameMetadata = {
	type: FrameType;
	status: FrameStatus;
	statusMessage: string;
	statusUpdatedAt?: Date; // wall-clock timestamp of the last updateFrameStatus call
	displayLabels?: Record<string, string>;
	sourceFrameIds?: string[];
	sourceChatId?: string;
	// generationOptions <-- we no longer use this field. Leaving it here commented out to prevent anyone from making another generationOptions (which would pull the old data from legacy frames - potentially leading to mayhem)
	backgroundColor?: string;
	publiclyReadable?: boolean; // if true, anyone can view this frame without needing access to the scene/etc
	visualEditActive?: boolean; // if true, show siteIds with its visual edits active (and treat it as if siteIds visual edits are its current state)
	veHash?: string | null; // hash of active visual edits; used for screenshot cache-busting on canvas when frame chat isn't loaded
	mode?: string | null; // active mode id (null/undefined = default)
	displayScale?: number; // visual scale multiplier for canvas display (default 1.0)
	provider?: string; // LLM provider used for generation (e.g. 'anthropic-bedrock', 'google-genai')
	model?: string; // LLM model used for generation (e.g. 'claude-sonnet-4-6', 'gemini-2.5-pro')
	durationSeconds?: number; // time taken to generate in seconds
	attemptNumber?: number; // 0 = first attempt, 1+ = retries
	chatVersion?: string; // set to FRAME_CHAT_VERSION once frame.entries have been migrated to frame_chat
};

export enum FrameType {
	GENERATE = 'generate',
	GENERATE_FROM_CHAT = 'generate-from-chat',
	ENHANCE = 'enhance',
	ADJUST = 'adjust',
	IMPORT = 'import',
	MERGE = 'merge',
	PROTOTYPE = 'prototype',
	IMPORT_SITE = 'import-site',
	// For testing and development purposes our systems will create frames from time to time.
	// These frames may not be associated with any of the user-facing flavors defined above.
	// So we use this type to keep track of them.
	SYSTEM = 'system',
}

export enum FrameStatus {
	PROCESSING = 'processing',
	ERROR = 'error',
	READY = 'ready',
}

//-----------------------------
// frame entries
//-----------------------------
export interface BaseFrameEntry {
	id: string;
	kind: string;
	createdAt: Date;
	deletedAt: Date | null;
}

export interface CommentFrameEntry extends BaseFrameEntry {
	kind: 'comment';
	message: string;
	userId: string;
	elementId?: string | null;
	siteId?: string;
	resolvedAt?: Date | null;
	parentId?: string | null;
	// Users mentioned via the picker when the comment was posted. The message
	// carries mentions as plain text ('@Ada Lovelace'); this id array is the
	// authoritative record — it drives the email send and mention rendering,
	// and is fixed at post time (edits never change it).
	mentionedUserIds?: string[];
}

export interface EditFrameEntry extends BaseFrameEntry {
	kind: 'edit';
	prompt: string;
	selectedDomElements: DomElement[];
}

export interface SiteFrameEntry extends BaseFrameEntry {
	kind: 'site';
	siteId: string;
	label?: string; // e.g. "Version 1"
	subLabel?: string; // e.g. "branched from Version 3"
}

export interface RecipeFrameEntry extends BaseFrameEntry {
	kind: 'recipe';
	// Recipe data needed to recreate GenerateSiteCommand for retry
	prompt: string;
	device: Device;
	codeToInclude?: string;
	referenceFrameIds: string[];
	chatEntries: ChatEntry[];
	style: string | null;
	wireframe: boolean;
	referenceImages?: string[];
	visualReferences?: string[];
	logPrefix: string;
	// for now, not including flowReferenceHtml. if we find we want better quality on retried flow frames we can extend this feature to include it
	// flowReferenceHtml?: string;
	statusString?: string;
	// Structured context from chat memory
	projectSummary?: string;
	guidelines?: string;
}

export interface VisualEditFrameEntry extends BaseFrameEntry {
	kind: 'visualEdit';
	siteId: string; // The siteId this visual edit applies to
	visualEdits: VisualEdit[];
	hash?: string; // Computed hash of visualEdits for fast lookup (denormalized) (specifically done so that Postgres can index on this value)
}

export type FrameEntry =
	| CommentFrameEntry
	| SiteFrameEntry
	| EditFrameEntry
	| RecipeFrameEntry
	| VisualEditFrameEntry;

//-----------------------------------------------------------------------------
// visual edits
//-----------------------------------------------------------------------------
export interface TransformVisualEdit {
	kind: 'transform';
	elementId: string;
	transform: { x: number; y: number };
}

export interface TextVisualEdit {
	kind: 'text';
	elementId: string;
	// legacy: pre-innerHTML edits, stored as plain text. read-only — new edits write innerHTML
	text?: string;
	innerHTML?: string;
}

export interface ImageVisualEdit {
	kind: 'image';
	elementId: string;
	imageUrl: string;
	altTag: string;
}

export interface StyleVisualEdit {
	kind: 'style';
	elementId: string;
	styles: Record<string, string>;
}

export interface DeleteVisualEdit {
	kind: 'delete';
	elementId: string;
}

export type VisualEdit =
	| TransformVisualEdit
	| TextVisualEdit
	| ImageVisualEdit
	| StyleVisualEdit
	| DeleteVisualEdit;

//-----------------------------------------------------------------------------
// chat
//-----------------------------------------------------------------------------

export interface SiteChatEntry {
	kind: 'site';
	siteId: string;
	label: string;
	subLabel?: string;
}

export interface SiteVeChatEntry {
	kind: 'site_ve';
	id: string;
	siteId: string;
	visualEdits: VisualEdit[];
	hash?: string;
}

export type ChatEntry = SystemChatEntry | UserChatEntry | SiteChatEntry | SiteVeChatEntry;
// A reference to an item in the DS editor that can be selected (analogous to Pick for the canvas).
// Each variant uses the canonical identifier from the DS file format itself.
export type DsPick =
	| { kind: 'token'; cssVariable: string }
	| { kind: 'component'; tagName: string }
	| { kind: 'recipe'; className: string }
	| { kind: 'guideline'; guidelinePath: string }
	| { kind: 'gallery'; galleryPath: string }
	| { kind: 'asset'; assetPath: string }
	| { kind: 'section'; section: string };

export interface SystemChatEntry {
	kind: 'system';
	message: string;
	restriction?: ChatEntryRestriction;
	feedback?: 'helpful' | 'not_helpful' | null;
	feedbackComment?: string | null;
	isSummary?: boolean;
}
export interface UserChatEntry {
	kind: 'user';
	message: string;
	sourceFrameIds?: string[];
	attachments?: Attachment[];
	selectedDomElements?: DomElement[]; // elements selected in frame inspector when prompt was submitted
	// it's kind of code smell these DS-related fields live here.
	// also it seems that the "vieweing" fields may not belong here at all (are they part of the entry per se?)
	// however they do affect how the chat is processed. so i'm gonne leave them here for now.
	selectedDsPicks?: DsPick[]; // picks the user has explicitly selected
	viewingDsPick?: DsPick; // a pick that is visible when chat is submitted (e.g. the tab that is open)
	viewingDsVersionNumber?: number; // set when viewing a historical DS version
}

export interface Attachment {
	filename: string;
	contentType: string;
	storagePath: string;
	size: number;
}

export type PathStatusMap = Record<string, 'clean' | 'dirty'>;

export type ChatMetadata = {
	status: ChatStatus;
	statusMessage: string;
	// Chat version (major.minor string) — '3.2' for scene, '3' for DS
	version?: string;
	// If true, the chat is archived and no new prompts can be sent
	readOnly?: boolean;
	// Points to the archived chat that this chat was upgraded from
	parentChatId?: string;
	// tracks DS file paths encountered by this chat (via ds_context, base-layer injection (e.g. theme/styles), or agent writes).
	// 'clean' = current version; 'dirty' = modified externally since last seen; absent = never encountered.
	filesInEntries?: PathStatusMap;
	// Project overview and brand identity (colors, fonts, visual direction).
	// Populated from conversation history, updated during compaction or by the agent.
	projectSummary?: string;
	// User preferences, "never do" rules, and key decisions made.
	// Populated from conversation history, updated during compaction or by the agent.
	guidelines?: string;
	// Summary of recent conversation context and project direction.
	// Covers entries before historySummaryUpTo — only recent entries are sent verbatim.
	historySummary?: string;
	// Index into entries[] where the summary ends — entries before this index are covered by historySummary.
	historySummaryUpTo?: number;
	// Total estimated tokens used in the context window for the last prompt
	contextTokens?: number;
};

export enum ChatStatus {
	PROCESSING = 'processing',
	STOPPABLE = 'stoppable',
	ERROR = 'error',
	READY = 'ready',
}

// Controls which audience an entry is visible to. Default (no restriction) is
// visible to both — studio renders it and the LLM sees it as a chat turn.
//
// USER: rendered in studio, hidden from the LLM. Used for entries that are
// purely user-facing UI artifacts (rare).
//
// LLM: hidden from studio, sent to the LLM as a *system-role* context message
// rather than as an assistant turn. This is the slot for orchestrator-emitted
// internal-state markers (e.g. plan-approved) — the LLM reads them as
// instructions/state, not as text patterns to mimic, and the user never sees
// the raw marker. See MessagesBuilder.tryAddChatEntries for the role mapping
// and DsConversation/SceneConversation render checks for the studio filter.
//
// History: this enum was used heavily in Chat 2.0, but became deprecated.
// We are using it now as a stopgap until DS chat can be migrated to the
// new scene-chat-style context building system.
export enum ChatEntryRestriction {
	USER = 'user',
	LLM = 'llm',
}

export enum ChatMode {
	DESIGN_AND_CHAT = 'design-and-chat', // Generates designs and responds with conversation
	// DESIGN_ONLY = 'design-only', // Only makes designs
	CHAT_ONLY = 'chat-only', // Only chats
}

export interface ChatRef {
	id: string;
	title: string;
	updatedAt: Date;
}

//-----------------------------------------------------------------------------
// chat versioning (major.minor — scene and DS evolve independently)
//-----------------------------------------------------------------------------

export const SCENE_CHAT_VERSION = '3.2';
export const DS_CHAT_VERSION = '3.1';
export const FRAME_CHAT_VERSION = '1.0';

// Returns true if `current` is older than `target` (or missing)
export function isOlderVersion(current: string | undefined, target: string): boolean {
	if (!current) return true;
	const [curMajor, curMinor = 0] = current.split('.').map(Number);
	const [tgtMajor, tgtMinor = 0] = target.split('.').map(Number);
	return curMajor < tgtMajor || (curMajor === tgtMajor && curMinor < tgtMinor);
}

//-----------------------------------------------------------------------------
// llm chat messages
//-----------------------------------------------------------------------------

export interface LlmChatMessage {
	id: string;
	chatId: string;
	type: 'user' | 'assistant' | 'tool_result';
	data: any; // JSONB — object for new rows, string for legacy rows
	compact: boolean;
	createdAt: Date;
	updatedAt: Date;
}

//-----------------------------------------------------------------------------
// scene comment
//-----------------------------------------------------------------------------

export type PartialSceneComment = Partial<SceneComment> & { id: string };

export interface SceneComment {
	id: string;
	sceneId: string;
	userId: string;
	x: number;
	y: number;
	text: string;
	metadata: SceneCommentMetadata;
	createdAt: Date;
	updatedAt?: Date;
	deletedAt: Date | null;
	writeId: string;
}

export type SceneCommentMetadata = {
	zoom: number;
	sortOrder: string;
	chatId?: string | null;
	chatScrollTop?: number;
	chatScrollHeight?: number;
};

//-----------------------------------------------------------------------------
// scene chat
//-----------------------------------------------------------------------------

export type PartialSceneChat = Partial<SceneChat> & { id: string };

export interface SceneChat {
	id: string;
	sceneId: string;
	title: string;
	x: number;
	y: number;
	entries: Array<ChatEntry>;
	metadata: ChatMetadata;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

//-----------------------------------------------------------------------------
// design system chat
//-----------------------------------------------------------------------------
export type PartialDsChat = Partial<DsChat> & { id: string };

export interface DsChat {
	id: string;
	designSystemId: string;
	title: string;
	entries: Array<ChatEntry>;
	metadata: ChatMetadata;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

//-----------------------------------------------------------------------------
// frame chat
//-----------------------------------------------------------------------------

export interface FrameChat {
	id: string;
	frameId: string;
	entries: Array<ChatEntry>;
	metadata: ChatMetadata;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

//-----------------------------------------------------------------------------
// device
//-----------------------------------------------------------------------------

// should we update frame to use this instead of x/y? this feels like it belongs
// as part of the Frame interface - ss
export interface Device {
	type: DeviceType | string; // e.g. "mobile", "tablet, "iPhone 16e", "80s console game"
	width: number;
	height: number;
}

export enum DeviceType {
	MOBILE = 'mobile',
	TABLET = 'tablet',
	DESKTOP = 'desktop',
	CUSTOM = 'custom',
}

export const DEFAULT_DEVICES: Device[] = [
	{ type: 'mobile', width: 430, height: 932 },
	{ type: 'tablet', width: 768, height: 1024 },
	{ type: 'desktop', width: 1728, height: 1117 },
];

//-----------------------------------------------------------------------------
// site
//-----------------------------------------------------------------------------
export type SiteMap = Record<string, string>;

export interface Site {
	id: string;
	sceneId: string;
	files: SiteMap;
	metadata: SiteMetadata;
}

export type SiteMetadata = {
	designSystemVersionId?: string;
	// Mode IDs the site accepts as query param. First (index 0) is default.
	modeIds?: string[];
};

export type Moonshot = {
	code: string;
	title: string;
	viewport: { width: number; height: number };
	fullPage: boolean;
	siteMap: SiteMap;
	url: string;
};

//-----------------------------------------------------------------------------
// google fonts
//-----------------------------------------------------------------------------
export interface GoogleFont {
	family: string;
	variants: string[];
	subsets: string[];
	version: string;
	lastModified: string;
	files: Record<string, string>;
	category: string;
	kind: string;
}

//-----------------------------------------------------------------------------
// site-related stuff
//-----------------------------------------------------------------------------

// DomElement is imported from './devigner' since it's used in both
// domain models (EditFrameEntry) and devigner IPC (SelectionInfo)
// Re-export for convenience so existing imports continue to work
export type { DomElement } from './devigner';

//-----------------------------------------------------------------------------
// Organization
//-----------------------------------------------------------------------------

export type OrganizationRole = 'owner' | 'editor';
export interface Organization {
	id: string;
	name: string;
	domains: string | null;
	stripeCustomerId?: string;
	metadata: Record<string, unknown>;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

export interface OrganizationUser {
	id: string;
	organizationId: string;
	userId: string;
	role: OrganizationRole;
	createdAt: Date;
	updatedAt: Date;
	writeId: string;
}

export type PartialOrganization = Partial<Organization> & { id: string };

export interface OrganizationSubscription {
	id: string;
	organizationId: string;
	stripeSubscriptionId?: string;
	tier: Tier;
	intervalType?: TierInterval;
	periodStartDate?: Date;
	nextCreditGrantDate?: Date;
	creditsGrantedCount: number;
	createdAt: Date;
	updatedAt: Date;
}

export interface OrganizationCreditWallet {
	organizationId: string;
	credits: number;
	promotionalCredits: number;
	purchaseCredits: number;
	createdAt: Date;
	updatedAt: Date;
}

//-----------------------------------------------------------------------------
// CreditWallet
//-----------------------------------------------------------------------------

export interface CreditWallet {
	userId: string;
	credits: number;
	promotionalCredits: number;
	purchaseCredits: number;
	createdAt: Date;
	updatedAt: Date;
	lastTopUpDate?: Date;
	lastPromotionalDate?: Date;
	creditRenewDate?: Date;
}

//-----------------------------------------------------------------------------
// CreditTransaction
//-----------------------------------------------------------------------------

export enum CreditTransactionType {
	CREDIT = 'credit',
	DEBIT = 'debit',
}

export interface CreditTransaction {
	userId: string;
	organizationId?: string | null;
	requestId?: string | null;
	actionType: string;
	type: CreditTransactionType;
	label?: string;
	amount: number;
	metadata?: Record<string, any>;
}
